import React, { useCallback, useEffect } from 'react';
import { ChatMediaPreviewModal } from './ChatMediaPreviewModal';
import { ChatContactForm, type ChatContactFormInput } from './ChatContactForm';
import { ChatPixForm, type ChatPixFormInput } from './ChatPixForm';
import { ChatLocationForm, type ChatLocationFormInput } from './ChatLocationForm';
import type { useChatAttachFlow } from '../../hooks/useChatAttachFlow';
import { useOrganizationPixKey } from '../../../settings/pix/hooks/useOrganizationPixKey';
import { usePermissions } from '../../../../hooks/usePermissions';
import type { PixKeyType as DomainPixKeyType } from '../../../../domain/pix/types';
import { toChatInput } from './chatPixInput';

type ChatAttachFlow = ReturnType<typeof useChatAttachFlow>;

interface ChatAttachOverlaysProps {
    attach: ChatAttachFlow;
    onSendContact?: (input: ChatContactFormInput) => Promise<void>;
    onSendLocation?: (input: ChatLocationFormInput) => Promise<void>;
    onSendPix?: (input: ChatPixFormInput) => Promise<void>;
}

export const ChatAttachOverlays: React.FC<ChatAttachOverlaysProps> = ({
    attach,
    onSendContact,
    onSendLocation,
    onSendPix,
}) => {
    const { pixKey, loading, save, refetch } = useOrganizationPixKey();
    const { canManagePixKey } = usePermissions();

    useEffect(() => {
        if (attach.specialMode === 'pix') {
            void refetch();
        }
    }, [attach.specialMode, refetch]);

    const handleContactSubmit = useCallback(async (input: ChatContactFormInput) => {
        if (onSendContact) {
            await onSendContact(input);
        }
        attach.closeSpecial();
    }, [onSendContact, attach]);

    const handleLocationSubmit = useCallback(async (input: ChatLocationFormInput) => {
        attach.closeSpecial();
        if (onSendLocation) {
            await onSendLocation(input);
        }
    }, [onSendLocation, attach]);

    const handlePixSubmit = useCallback(async (input: ChatPixFormInput) => {
        if (!onSendPix) {
            throw new Error('Envio de PIX indisponível.');
        }
        // Defesa em profundidade: se há chave no banco, ignora payload forjado no cliente.
        // A fonte de verdade final continua no uazapi-proxy (enforceOrgPixPayload).
        const trusted: ChatPixFormInput = pixKey
            ? toChatInput(pixKey.pixKey, pixKey.pixType, pixKey.pixName)
            : input;
        await onSendPix(trusted);
        attach.closeSpecial();
    }, [onSendPix, attach.closeSpecial, pixKey]);

    const handlePixSaveAndSend = useCallback(async (input: ChatPixFormInput) => {
        const saved = await save({
            pixKey: input.pixKey,
            pixType: input.type.toLowerCase() as DomainPixKeyType,
            pixName: input.name?.trim() || '',
        });
        await handlePixSubmit(toChatInput(saved.pixKey, saved.pixType, saved.pixName));
    }, [save, handlePixSubmit]);

    return (
        <>
            {attach.isPreviewOpen && attach.sessionKind && (
                <ChatMediaPreviewModal
                    isOpen={attach.isPreviewOpen}
                    sessionKind={attach.sessionKind}
                    items={attach.staged}
                    caption={attach.caption}
                    onCaptionChange={attach.setCaption}
                    onRemove={attach.removeStaged}
                    onAddMore={() => {
                        if (attach.sessionKind === 'media') {
                            attach.openMediaPicker();
                        } else {
                            attach.openDocumentPicker();
                        }
                    }}
                    onClose={attach.closePreview}
                    onSend={attach.confirmSend}
                />
            )}

            <ChatContactForm
                isOpen={attach.specialMode === 'contact'}
                onClose={attach.closeSpecial}
                onSubmit={handleContactSubmit}
            />
            <ChatPixForm
                isOpen={attach.specialMode === 'pix'}
                onClose={attach.closeSpecial}
                onSubmit={handlePixSubmit}
                onSaveAndSend={handlePixSaveAndSend}
                savedKey={pixKey}
                loading={loading}
                canManage={canManagePixKey}
            />
            <ChatLocationForm
                isOpen={attach.specialMode === 'location'}
                onClose={attach.closeSpecial}
                onSubmit={handleLocationSubmit}
            />
        </>
    );
};
